import type { ImportRulePayload } from '@/features/wizard-evals/api/wizard-evals-api';

/**
 * Parse a single CSV line, respecting double-quoted fields and the `""` escape.
 */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

const ID_ALIASES = new Set(['ruleid', 'rule_id', 'id']);
const TEXT_ALIASES = new Set(['ruletext', 'rule_text', 'label', 'text']);
const CARRIER_ALIASES = new Set(['carrier']);
const CATEGORY_ALIASES = new Set(['category']);
const ACTIVE_ALIASES = new Set(['isactive', 'is_active', 'active']);
const KEYWORDS_ALIASES = new Set(['keywords']);

type ColumnKind =
  | 'ruleId'
  | 'carrier'
  | 'category'
  | 'ruleText'
  | 'isActive'
  | 'keywords'
  | null;

function classifyHeader(raw: string): ColumnKind {
  const h = raw.trim().toLowerCase();
  if (ID_ALIASES.has(h)) return 'ruleId';
  if (CARRIER_ALIASES.has(h)) return 'carrier';
  if (CATEGORY_ALIASES.has(h)) return 'category';
  if (TEXT_ALIASES.has(h)) return 'ruleText';
  if (ACTIVE_ALIASES.has(h)) return 'isActive';
  if (KEYWORDS_ALIASES.has(h)) return 'keywords';
  return null;
}

function parseKeywordsCell(raw: string): string[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map(v => String(v).trim()).filter(Boolean);
      }
    } catch {
      // fall through to delimiter split
    }
  }
  return trimmed
    .split(/[|;]/)
    .map(k => k.trim())
    .filter(Boolean);
}

export interface ParseRulesCsvResult {
  rules: ImportRulePayload[];
  errors: string[];
}

export function parseRulesCsv(csvText: string): ParseRulesCsvResult {
  const lines = csvText
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter(line => line.length > 0);

  if (lines.length === 0) {
    return { rules: [], errors: ['CSV is empty.'] };
  }

  const headerFields = parseCsvLine(lines[0]).map(h => h.trim());
  const columnMap: ColumnKind[] = headerFields.map(classifyHeader);

  if (!columnMap.includes('ruleId')) {
    return { rules: [], errors: ['Missing required column: ruleId (or rule_id).'] };
  }
  if (!columnMap.includes('carrier')) {
    return { rules: [], errors: ['Missing required column: carrier.'] };
  }
  if (!columnMap.includes('ruleText')) {
    return { rules: [], errors: ['Missing required column: ruleText (or label).'] };
  }

  const rules: ImportRulePayload[] = [];
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i]);
    const row: Record<string, string> = {};
    columnMap.forEach((kind, idx) => {
      if (kind) row[kind] = (fields[idx] ?? '').trim();
    });

    if (!row.ruleId) {
      errors.push(`Row ${i + 1}: missing ruleId`);
      continue;
    }
    if (!row.carrier) {
      errors.push(`Row ${i + 1}: missing carrier`);
      continue;
    }
    if (!row.ruleText) {
      errors.push(`Row ${i + 1}: missing ruleText`);
      continue;
    }

    rules.push({
      ruleId: row.ruleId,
      carrier: row.carrier.toLowerCase(),
      category: row.category ? row.category : null,
      ruleText: row.ruleText,
      keywords: parseKeywordsCell(row.keywords ?? ''),
      isActive:
        row.isActive == null || row.isActive === ''
          ? true
          : row.isActive.toLowerCase() !== 'false',
    });
  }

  return { rules, errors };
}

export interface RuleDiff {
  adds: ImportRulePayload[];
  updates: ImportRulePayload[];
  deactivations: { ruleId: string; carrier: string; ruleText: string }[];
}

export function diffRules(
  incoming: ImportRulePayload[],
  existing: Array<{ ruleId: string; carrier: string; ruleText: string; isActive: boolean }>,
): RuleDiff {
  const incomingIds = new Set(incoming.map(r => r.ruleId));
  const carriersInScope = new Set(incoming.map(r => r.carrier));
  const existingById = new Map(existing.map(r => [r.ruleId, r]));

  const adds: ImportRulePayload[] = [];
  const updates: ImportRulePayload[] = [];
  for (const rule of incoming) {
    if (existingById.has(rule.ruleId)) updates.push(rule);
    else adds.push(rule);
  }

  const deactivations = existing
    .filter(
      r =>
        carriersInScope.has(r.carrier) &&
        r.isActive &&
        !incomingIds.has(r.ruleId),
    )
    .map(r => ({ ruleId: r.ruleId, carrier: r.carrier, ruleText: r.ruleText }));

  return { adds, updates, deactivations };
}
